/**
 * EspoCRM - Base API Configuration
 * 
 * This module configures the RTK Query API instance for the EspoCRM.
 */

import {
    createApi,
    BaseQueryFn,
    FetchArgs,
    fetchBaseQuery,
    FetchBaseQueryError
} from '@reduxjs/toolkit/query/react';
import { checkIfStatusIsForbidden } from 'src/core/features/auth/authRedirect';
import { getGlobalConfig } from 'src/core/config';
import { RootState } from 'src/store';
import qs from 'qs';

// Lazy creation of baseQuery to avoid accessing globalConfig before initialization
let cachedBaseQuery: ReturnType<typeof fetchBaseQuery> | null = null;

const getBaseQuery = () => {
    if (!cachedBaseQuery) {
        cachedBaseQuery = fetchBaseQuery({
            baseUrl: getGlobalConfig().api.url,
            prepareHeaders: (headers, { getState }) => {
                const token = (getState() as RootState).auth.token;
                const username = (getState() as RootState).auth.username;
                if (token && headers.get('authorization') !== 'Basic dXNlcnNpdGU6SWRfcVc1bTgzSEw=') {
                    headers.set('authorization', `Basic ${btoa(username + ':' + token)}`);
                }
                headers.set('cache-control', 'no-cache');
                headers.set('pragma', 'no-cache');
                return headers;
            },
            paramsSerializer: (params) => {
                return qs.stringify(params);
            }
        });
    }
    return cachedBaseQuery;
};

const baseQueryWithAuthCheck: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    const baseQuery = getBaseQuery();
    let result = await baseQuery(args, api, extraOptions);
    if (result.error && !(result.meta?.request?.url?.includes('UserSecurity/password'))) {
        checkIfStatusIsForbidden(result.error);
    }
    return result;
};

export const emptySplitApi = createApi({
    reducerPath: 'drherancaApi',
    baseQuery: baseQueryWithAuthCheck,
    endpoints: (build) => ({
        getUserInfo: build.query<GetUserInfoApiResponse, void>({
            query: () => ({
                url: `App/user`,
                method: 'GET',
                headers: {
                    'content-type': 'application/json'
                }
            })
        }),
        createUser: build.mutation<CreateUserApiResponse, CreateUserApiArg>({
            query: (queryArg) => ({
                url: `UserManager/action/create`,
                method: 'POST',
                headers: {
                    'accept': '*/*',
                    'content-type': 'application/json',
                    'authorization': 'Basic dXNlcm1hbmFnZXI6SE1qPzMyODJhVUk='
                },
                body: {
                    name: queryArg.name,
                    company: queryArg.company,
                    email: queryArg.email,
                    password: queryArg.password
                }
            })
        }),
        loginEspoCrm: build.mutation<GetUserInfoApiResponse, GetUserInfoApiArg>({
            query: (queryArg) => ({
                url: `App/user`,
                method: 'GET',
                headers: {
                    'authorization': `Basic ${btoa(queryArg.body.username + ':' + queryArg.body.password)}`,
                }
            })
        })
    })
});

// Export the hooks
export const { useGetUserInfoQuery, useCreateUserMutation, useLoginEspoCrmMutation } = emptySplitApi;

// Type definitions
export type CreateUserApiArg = {
    name: string;
    company: string;
    email: string;
    password: string;
};

export type CreateUserApiResponse = {
    id?: string;
    name?: string;
    email?: string;
    status?: number;
    message?: string;
};

export type GetUserInfoApiArg = {
    body: {
        username: string;
        password: string;
    };
};

export type GetUserInfoApiResponse = {
    user?: {
        id: string;
        name: string;
        deleted: boolean;
        userName: string;
        type: string;
        salutationName: string;
        firstName: string;
        lastName: string;
        isActive: boolean;
        title: string | null;
        emailAddress: string | null;
        phoneNumber: string | null;
        ipAddress: string;
        avatarColor: string | null;
        createdAt: string;
        modifiedAt: string;
        middleName: string | null;
        emailAddressIsOptedOut: boolean | null;
        emailAddressIsInvalid: boolean | null;
        phoneNumberIsOptedOut: boolean | null;
        phoneNumberIsInvalid: boolean | null;
        defaultTeamId: string | null;
        defaultTeamName: string | null;
        teamsIds: string[];
        teamsNames: Record<string, string>;
        teamsColumns: Record<string, unknown>;
        contactId: string | null;
        contactName: string | null;
        avatarId: string | null;
        avatarName: string | null;
        createdById: string;
        createdByName: string;
        workingTimeCalendarId: string | null;
        emailAddressList: string[];
        userEmailAddressList: string[];
        excludeFromReplyEmailAddressList: string[];
    };
    preferences?: {
        weekStart: number;
        thousandSeparator: string;
        decimalMark: string;
        dashboardLayout: Array<{
            name: string;
            layout: Array<{
                id: string;
                name: string;
                x: number;
                y: number;
                width: number;
                height: number;
            }>;
        }>;
        dashletsOptions: Record<string, unknown>;
        exportDelimiter: string;
    };
    token: string;
    settings?: {
        useCache: boolean;
        applicationName: string;
        version: string;
        timeZone: string;
        dateFormat: string;
        timeFormat: string;
        weekStart: number;
        thousandSeparator: string;
        decimalMark: string;
        exportDelimiter: string;
        currencyList: string[];
        defaultCurrency: string;
        baseCurrency: string;
        currencyRates: unknown[];
        outboundEmailIsShared: boolean;
        language: string;
    };
    language?: string;
    status?: number;
    message?: string;
};
